import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Container, Box, Grid, Typography, Alert, Card, CardContent, Button } from "@mui/material";
import { Assignment, Add } from "@mui/icons-material";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import OpportunityDetail from "../components/OpportunityDetail";
import AgentTaskChecklist from "../components/AgentTaskChecklist";
import { useRole } from "../domain/roles/role.middleware";
import { TaskGuard } from "../domain/roles/role.guard";
import { useOpportunityAccess } from "../domain/roles/role.middleware";
// import { useAuth } from "../context/AuthContext";
// import { useTranslation } from "react-i18next";
// import { investmentService } from "../investments/investmentService";
// import { activityService } from "../activity/activityService";
// import { formatCurrency } from "../capital/capitalUtils";
// TODO(api): Replace direct captationService import with API call
// import { captationService } from '../services/captationService';

const OpportunityDetailPage = () => {
    const { id } = useParams();
    const [opportunity, setOpportunity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTasks, setRefreshTasks] = useState(false);
    const [hasTasks, setHasTasks] = useState(false);
    // const [investmentAmount, setInvestmentAmount] = useState("");
    // const [investmentDialogOpen, setInvestmentDialogOpen] = useState(false);
    // const [submitting, setSubmitting] = useState(false);
    // const [activeTab, setActiveTab] = useState(0);
    // const [projectTasks, setProjectTasks] = useState([]);
    // const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    // const [selectedTask, setSelectedTask] = useState(null);
    // const [taskUpdate, setTaskUpdate] = useState("");
    // const [snackbarOpen, setSnackbarOpen] = useState(false);
    // const [snackbarMessage, setSnackbarMessage] = useState("");
  
    const { roleService, isInitialized } = useRole();
    const { canView, canEdit } = useOpportunityAccess(opportunity);

    useEffect(() => {
        const fetchOpportunity = async () => {
            try {
                setLoading(true);
                // Get all fake opportunities and find the one with matching ID
                const opportunity = await opportunitiesService.getOpportunityById(id);
        
                if (opportunity) {
                    setOpportunity(opportunity);
                    // Check if there are tasks for this project
                    await checkProjectTasks(opportunity.id);
                } else {
                    setError("Opportunity not found");
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchOpportunity();
        }
    }, [id, checkProjectTasks]);

    const checkProjectTasks = useCallback(async (projectId) => {
        try {
            if (roleService.currentUser) {
                // TODO(api): Replace captationService usage with API call
                // // TODO(api): Replace captationService usage with API call
                // const agentTasks = await captationService.getAgentTasks(roleService.currentUser.uid);
                const agentTasks = []; // Placeholder until API is implemented
                const projectTasks = agentTasks.filter(task => task.projectId === projectId);
                setHasTasks(projectTasks.length > 0);
            }
        } catch (error) {
            // console.error("Error checking project tasks:", error);
            setHasTasks(false);
        }
    }, [roleService.currentUser]);

    const handleTaskUpdate = async () => {
        setRefreshTasks(prev => !prev);
        // Re-check tasks after update
        if (opportunity) {
            await checkProjectTasks(opportunity.id);
        }
    };

    const handleRequestTasks = async () => {
        try {
            // This would typically send a notification to the project coordinator
            alert("Solicitud de tareas enviada al coordinador del proyecto");
        } catch (error) {
            // console.error("Error requesting tasks:", error);
        }
    };

    const isAgentAssigned = () => {
        if (!opportunity || !roleService.currentUser) return false;
    
        // Check if current user is the agent assigned to this opportunity
        return opportunity.agentId === roleService.currentUser.uid;
    };

    const shouldShowTaskChecklist = () => {
    // Show checklist if user is admin, reviewer, or the assigned agent
        const currentRole = roleService.getCurrentUserRole();
        return ["ADMIN", "REVIEWER"].includes(currentRole) || isAgentAssigned();
    };

    const canRequestTasks = () => {
    // Only assigned agents can request tasks
        return isAgentAssigned() && !hasTasks;
    };

    if (!isInitialized) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography>Cargando permisos...</Typography>
            </Container>
        );
    }

    if (error === "Opportunity not found") {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="warning">
                    <Typography variant="h6">Oportunidad No Encontrada</Typography>
                    <Typography>
            La oportunidad con ID &quot;{id}&quot; no existe. Verifica la URL o navega desde la lista de oportunidades.
                    </Typography>
                </Alert>
            </Container>
        );
    }

    if (opportunity && !canView) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    <Typography variant="h6">Acceso Denegado</Typography>
                    <Typography>
            No tienes permisos para ver esta oportunidad.
                    </Typography>
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
                {/* Main opportunity details */}
                <Grid item xs={12} lg={8}>
                    <OpportunityDetail 
                        opportunity={opportunity}
                        loading={loading}
                        error={error}
                        canEdit={canEdit}
                    />
                </Grid>

                {/* Task checklist sidebar */}
                <Grid item xs={12} lg={4}>
                    <TaskGuard action="view">
                        {shouldShowTaskChecklist() && (
                            <Box sx={{ position: "sticky", top: 20 }}>
                                {hasTasks ? (
                                    <AgentTaskChecklist 
                                        projectId={id}
                                        onTaskUpdate={handleTaskUpdate}
                                        key={refreshTasks} // Force re-render when tasks are updated
                                    />
                                ) : canRequestTasks() ? (
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ textAlign: "center", py: 3 }}>
                                                <Assignment sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                                                <Typography variant="h6" gutterBottom>
                          No hay tareas asignadas
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                          Solicita tareas al coordinador del proyecto para comenzar a trabajar en esta oportunidad.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Add />}
                                                    onClick={handleRequestTasks}
                                                    fullWidth
                                                >
                          Solicitar Tareas
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ textAlign: "center", py: 3 }}>
                                                <Assignment sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                                                <Typography variant="h6" gutterBottom>
                          Gestión de Tareas
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {isAgentAssigned() 
                                                        ? "No tienes tareas asignadas para este proyecto."
                                                        : "Las tareas aparecerán aquí cuando sean asignadas."
                                                    }
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </Box>
                        )}
                    </TaskGuard>
                </Grid>
            </Grid>
        </Container>
    );
};

export default OpportunityDetailPage; 